import Api from '@/service/api';
import Auth from '@/service/auth';

import isValidSession from '@/helpers/check-session';

import Checkbox from '@/components/Form/Checkbox/index.vue';
import Input from '@/components/Form/Input/index.vue';
import MultiloginInput from '@/components/Form/MultiloginInput/index.vue';
import Success from '@/views/Oauth/Authorize/Success/index.vue';


export default {
  name: 'OauthAuthorize',

  components: {
    Checkbox,
    Input,
    MultiloginInput,
    Success,
  },

  mounted() {
    const oauthData = this.$store.getters.oauth2Data;

    this.redirectUri = this.$route.query.redirect_uri || oauthData.redirectUri || '';
    oauthData.redirectUri = this.redirectUri;

    if (this.$route.query.response_type || oauthData.responseType) {
      this.responseType = this.$route.query.response_type || oauthData.responseType;
      oauthData.responseType = this.responseType;
    }

    if (this.$route.query.client_id || oauthData.clientId) {
      this.clientId = this.$route.query.client_id || oauthData.clientId;
      oauthData.clientId = this.clientId;
    }

    if (this.$route.query.state || oauthData.state) {
      this.state = this.$route.query.state || oauthData.state;
      oauthData.state = this.state;
    }

    if (this.$route.query.code_challenge || oauthData.codeChallenge) {
      this.codeChallenge = this.$route.query.code_challenge || oauthData.codeChallenge;
      oauthData.codeChallenge = this.codeChallenge;
    }

    if (this.$route.query.code_challenge_method || oauthData.codeChallengeMethod) {
      this.codeChallengeMethod = this.$route.query.code_challenge_method
        || oauthData.codeChallengeMethod;
      oauthData.codeChallengeMethod = this.codeChallengeMethod;
    }

    if (!this.clientId) {
      this.toSignIn();
    }

    this.$store.commit('saveOauth2Data', oauthData);
    this.getClient();
  },

  methods: {
    toSignIn() {
      this.$router.push({
        name: 'SignIn',
      });
    },

    async getUser() {
      let response = null;

      if (!this.sessionId) {
        return;
      }

      try {
        response = await Api.post({
          url: 'oauth2/get-user',
          data: {
            session_id: this.sessionId.replace(/"/g, ''),
          },
          preloader: false,
        });
      } catch (error) {
        return;
      }

      this.user = response;
    },

    async getClient() {
      try {
        this.client = await Api.post({
          url: 'oauth2/get-client',
          data: {
            client_id: this.clientId,
            response_type: this.responseType,
            redirect_uri: this.redirectUri,
            state: this.state,
            code_challenge: this.codeChallenge,
            code_challenge_method: this.codeChallengeMethod,
          },
          preloader: false,
        });
      } catch (error) {
        window.console.log('can\'t get client');
        this.toSignIn();
      }

      if (this.client.logo_url === '/styles/images/default_logo.png') {
        this.imageExist = false;
        return;
      }

      const url = `https://${window.location.hostname}`;

      if (this.client.logo_url) {
        try {
          await fetch(`${url}${this.client.logo_url}`);
        } catch (error) {
          this.imageExist = false;
          return;
        }

        this.imageExist = true;
      }
    },

    async signIn() {
      if (!this.validate()) {
        return false;
      }

      this.isSignInDisabled = true;

      try {
        await Api.post({
          url: 'sign-in',
          data: {
            login: this.login,
            password: this.password,
            remember_me: this.isChecked,
          },
          preloader: false,
        });
      } catch (error) {
        this.isSignInDisabled = false;

        if (error.status === 401) {
          this.validated = false;
          this.isError = true;
          this.errorCode = error.code;

          return false;
        }

        this.isServerError = true;
        return false;
      }

      return true;
    },

    validate() {
      if (!this.$refs.multiLogin.validate()) {
        this.validated = false;
      }
      if (!this.$refs.password.validate()) {
        this.validated = false;
      }

      return this.validated;
    },

    async onAllow() {
      if (!this.isAuth) {
        const isSignIn = await this.signIn();

        if (!isSignIn) {
          return;
        }
      }

      this.sessionId = Auth.getSession();
      this.isSignInDisabled = true;
      let response = null;

      try {
        response = await Api.post({
          url: 'oauth2/allow',
          data: {
            client_id: this.clientId,
            request_id: this.client.request_id,
            session_id: this.sessionId.replace(/"/g, ''),
          },
        });
      } catch (error) {
        this.isSignInDisabled = false;

        if (error.status === 400) {
          this.isError = true;
          this.errorCode = error.code;

          return;
        }

        this.isServerError = true;

        return;
      }

      this.successRedirectUrl = response.auth_uri;
      this.isPermissionsGranted = true;
      window.location.href = response.auth_uri;
    },

    onCancel() {
      const cancelUri = `${this.redirectUri || this.clientId.redirect_uri}?${this.state ? `state=${this.state}&` : ''}error=access_denied`;
      window.location.href = cancelUri;
    },

    fieldsChanged() {
      this.validated = true;
      this.isError = false;
      this.isServerError = false;
    },

    toRestoration() {
      this.$store.commit('setNeedReturnToOAuth2', true);
      this.$router.push('/password/restoration');
    },

    async onSignOut() {
      try {
        await Api.post({
          url: 'sign-out',
          data: {
            session_id: this.sessionId,
          },
        });
      } catch (error) {
        window.console.log(error);
      }

      this.isAuth = Auth.isAuthenticated();
    },
  },

  async created() {
    this.$store.commit('setNeedReturnToOAuth2', false);
    this.sessionId = Auth.getSession();

    if (this.sessionId) {
      this.isAuth = true;
      const isValidSessionId = await isValidSession(this.sessionId);

      if (isValidSessionId) {
        this.getUser();
      } else {
        this.isAuth = false;
      }
    }
  },

  data() {
    return {
      client: {},
      clientId: null,
      isAuth: false,
      isChecked: false,
      isError: false,
      isPermissionsGranted: false,
      isServerError: false,
      isSignInDisabled: false,
      imageExist: false,
      password: null,
      responseType: null,
      successRedirectUrl: '',
      user: {},
      login: null,
    };
  },
};
